/**
 * Client-side mirror of the active-window arithmetic (RET-1, RET-2, RET-12).
 *
 * **A mirror, not the source.** The API owns expiry — it stamps `expiresAt` and
 * the sweep acts on it. This module only decides whether to *show* an
 * "expiring soon" hint on a row the API already dated, so a small drift after a
 * window change (the row heals on the next sweep) is a soft hint being briefly
 * early, never an authorization. The formula matches `shared/files.ts`'s
 * `warnLeadFor` so the hint appears exactly when the server would warn.
 */

const MS_PER_DAY = 86_400_000;

/** RET-1's default window; a room reads absence of `expiryWindowDays` as this. */
export const ACTIVE_WINDOW_DEFAULT_DAYS = 180;

/** RET-2's default lead, and the cap `warnLeadFor` never exceeds. */
export const EXPIRY_WARN_DAYS = 30;

/**
 * How many days before expiry a room's files start showing the hint (RET-12).
 *
 * Half the window, at least a day and at most 30 — the server's `warnLeadFor`
 * exactly. The `max(1, …)` floor is why a 1-day room still flags its files
 * rather than showing nothing.
 */
export function warnLeadFor(windowDays: number = ACTIVE_WINDOW_DEFAULT_DAYS): number {
  return Math.min(EXPIRY_WARN_DAYS, Math.max(1, Math.floor(windowDays / 2)));
}

/** Whole days from now until `expiresAt`, floored at 0. */
export function daysUntil(expiresAt: string, now: number = Date.now()): number {
  const ms = Date.parse(expiresAt) - now;
  return Math.max(0, Math.floor(ms / MS_PER_DAY));
}

/**
 * Whether a file's row should show the "expiring soon" hint (RET-3 as amended).
 *
 * True once `now` is within the room's warn lead of the file's `expiresAt`.
 * Absent `expiresAt` (a row not yet stamped) shows nothing, like the server's
 * own "not yet due".
 */
export function isExpiringSoon(
  expiresAt: string | undefined,
  windowDays: number = ACTIVE_WINDOW_DEFAULT_DAYS,
  now: number = Date.now(),
): boolean {
  if (expiresAt === undefined) return false;
  const leadMs = warnLeadFor(windowDays) * MS_PER_DAY;
  return Date.parse(expiresAt) - now <= leadMs;
}
